<template>
  <vs-popup :title="popupTitle" :active.sync="internalActive">
    <div class="nxp-referral-popup-content nxp-referral-modal-body">
      <p class="nxp-referral-modal-desc">
        <template v-if="isEdit">
          Atualize os dados da pessoa que te indicou para a vaga
          <strong>{{ vacancyTitle }}</strong>
          na empresa
          <strong>{{ companyName }}</strong>. Se você alterar o e-mail, enviaremos
          uma nova confirmação para o novo destinatário.
        </template>
        <template v-else>
          Você está se declarando como indicado para a vaga
          <strong>{{ vacancyTitle }}</strong>
          na empresa
          <strong>{{ companyName }}</strong>. Enviaremos um e-mail de confirmação
          para a pessoa que você informar abaixo.
        </template>
      </p>
      <div class="nxp-referral-modal-warning">
        <info-icon size="24" class="nxp-referral-modal-warning-icon" />
        <div>
          <h4 class="nxp-referral-modal-warning-title">
            Este espaço é exclusivo para programas de indicação, não para
            referências
          </h4>
          <p class="nxp-referral-modal-warning-text">
            Informe a indicação somente se um(a) colaborador(a) indicou você,
            pois um e-mail será enviado a essa pessoa para confirmar a indicação.
          </p>
        </div>
      </div>
      <validation-observer ref="observer" tag="form" @submit.prevent="submit">
        <div class="nxp-referral-modal-fields">
          <validation-provider
            class="w-full"
            name="nome"
            rules="required"
            v-slot="{ errors }"
          >
            <label class="nxp-referral-field-label" for="referral-name">
              Nome de quem te indicou
            </label>
            <vs-input
              id="referral-name"
              v-model="name"
              class="w-full"
              type="text"
              :danger="errors.length > 0"
            />
            <span v-if="errors[0]" class="nxp-referral-field-error">{{
              errors[0]
            }}</span>
          </validation-provider>
          <validation-provider
            class="w-full"
            name="email"
            rules="required|email"
            v-slot="{ errors }"
          >
            <label class="nxp-referral-field-label" for="referral-email">
              E-mail de quem te indicou
            </label>
            <vs-input
              id="referral-email"
              v-model="email"
              class="w-full"
              type="email"
              :danger="errors.length > 0"
            />
            <span v-if="errors[0]" class="nxp-referral-field-error">{{
              errors[0]
            }}</span>
          </validation-provider>
        </div>
        <div class="nxp-referral-modal-actions">
          <button
            type="button"
            class="nxp-referral-modal-btn-cancel"
            @click="cancel"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="nxp-referral-modal-btn-submit"
            :style="{ background: color }"
          >
            {{ submitLabel }}
          </button>
        </div>
      </validation-observer>
    </div>
  </vs-popup>
</template>

<script>
import { get } from "lodash";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { InfoIcon } from "vue-feather-icons";

const MESSAGE_BY_STATUS = {
  400: "Não foi possível enviar o formulário.",
  403: "Você não tem permissão para alterar essa indicação.",
  404: "Não encontramos a indicação que você está procurando.",
  406: "Sua indicação já foi confirmada.",
  409: "Você já sugeriu essa indicação.",
};

export default {
  name: "ReferralModal",
  components: { ValidationObserver, ValidationProvider, InfoIcon },
  props: {
    active: { type: Boolean, required: true },
    vacancyId: { type: [Number, String], required: true },
    vacancyTitle: { type: String, default: "" },
    companyName: { type: String, default: "esta empresa" },
    color: { type: String, default: "#1551b1" },
    mode: {
      type: String,
      default: "create",
      validator: (v) => ["create", "edit"].includes(v),
    },
    indicationUuid: { type: String, default: "" },
    initialName: { type: String, default: "" },
    initialEmail: { type: String, default: "" },
  },
  data: () => ({ name: "", email: "" }),
  computed: {
    internalActive: {
      get() { return this.active; },
      set(val) { this.$emit("update:active", val); },
    },
    isEdit() {
      return this.mode === "edit";
    },
    popupTitle() {
      return this.isEdit ? "Editar indicação" : "Declare sua indicação";
    },
    submitLabel() {
      return this.isEdit ? "Salvar alterações" : "Declarar indicação";
    },
  },
  watch: {
    active(open) {
      if (open) this.prefill();
      else this.reset();
    },
  },
  created() {
    if (this.active) this.prefill();
  },
  methods: {
    cancel() {
      this.internalActive = false;
    },
    prefill() {
      this.name = this.initialName || "";
      this.email = this.initialEmail || "";
      this.$nextTick(() => {
        if (this.$refs.observer) this.$refs.observer.reset();
      });
    },
    reset() {
      this.name = "";
      this.email = "";
      this.$nextTick(() => {
        if (this.$refs.observer) this.$refs.observer.reset();
      });
    },
    async submit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      this.$vs.loading({ text: this.isEdit ? "Salvando..." : "Enviando..." });
      try {
        const declared = { name: this.name, email: this.email };

        if (this.isEdit) {
          const emailChanged =
            (this.initialEmail || "").trim().toLowerCase() !==
            this.email.trim().toLowerCase();

          await this.$http.put(`/api/v1/indications/${this.indicationUuid}`, {
            name: this.name,
            email: this.email,
          });

          this.$emit("updated", { ...declared, emailChanged });
          this.internalActive = false;

          try {
            const { trackEvent } = await import("@/services/posthog");
            trackEvent("referral_updated", {
              referrer_name: declared.name,
              referrer_email: declared.email,
              email_changed: emailChanged,
              vacancy_id: this.vacancyId,
              source: "dashboard_applications",
            });
          } catch (_) { /* posthog optional */ }

          this.$vs.notify({
            text: emailChanged
              ? "Indicação atualizada! Uma nova confirmação foi enviada."
              : "Indicação atualizada com sucesso!",
            title: "Sucesso!",
            icon: "icon-alert-circle",
            color: "success",
            iconPack: "feather",
            position: "top-right",
            time: 5000,
          });
        } else {
          const response = await this.$http.post(
            "/api/v1/indications?debug=true",
            {
              vacancy_id: this.vacancyId,
              name: this.name,
              email: this.email,
            }
          );

          const created = get(response, "data.data", {}) || {};

          this.$emit("declared", {
            ...declared,
            uuid: created.uuid || "",
            status:
              created.status === undefined ? null : created.status,
            validated_at:
              created.validated_at === undefined ? null : created.validated_at,
          });
          this.internalActive = false;

          try {
            const { trackEvent } = await import("@/services/posthog");
            trackEvent("referral_created", {
              referrer_name: declared.name,
              referrer_email: declared.email,
              vacancy_id: this.vacancyId,
              source: "dashboard_applications",
            });
          } catch (_) { /* posthog optional */ }

          this.$vs.notify({
            text: "A indicação foi solicitada com sucesso!",
            title: "Sucesso!",
            icon: "icon-alert-circle",
            color: "success",
            iconPack: "feather",
            position: "top-right",
            time: 5000,
          });
        }
      } catch (error) {
        const status = get(error, "response.status", 0);
        const message =
          MESSAGE_BY_STATUS[status] ||
          get(error, "response.data.message", "Não foi possível enviar o formulário.");
        this.$vs.notify({
          text: message,
          title: "Ops!",
          icon: "icon-alert-circle",
          color: "warning",
          iconPack: "feather",
          position: "top-right",
          time: 5000,
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>
